/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package models

import utils.getCurrentInstant
import utils.toLocalDate
import kotlin.js.Date as JsDate

/**
 * Factory function for creating SavingsGoal from JavaScript Date objects
 * This avoids webApp needing direct access to kotlinx-datetime types
 */
fun createSavingsGoalFromJs(
    id: String,
    name: String,
    targetAmount: Double,
    currentAmount: Double,
    category: GoalCategory,
    priority: GoalPriority,
    deadline: JsDate,
    iconEmoji: String,
): SavingsGoal {
    val localDate = deadline.toLocalDate()
    // Format as "Jan 15, 2026" for display
    val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val deadlineStr = "${monthNames[localDate.monthNumber - 1]} ${localDate.dayOfMonth}, ${localDate.year}"

    return SavingsGoal(
        id = id,
        name = name,
        targetAmount = targetAmount,
        currentAmount = currentAmount,
        category = category,
        priority = priority,
        deadline = localDate,
        createdDate = getCurrentInstant(),
        isActive = true,
        iconEmoji = iconEmoji,
        deadlineStr = deadlineStr,
    )
}

/**
 * Extension function to get days remaining as Int
 */
fun SavingsGoal.getDaysRemaining(): Int {
    val today = JsDate()
    val todayEpoch = (today.getTime() / (1000.0 * 60 * 60 * 24)).toInt()
    val deadlineEpoch = deadline.toEpochDays()
    return deadlineEpoch - todayEpoch
}
